// Worker との最小 RPC。要求ごとに id を振り、Transferable を渡せる。
// Worker が使えない環境 (テスト / 古いブラウザ) ではフォールバック関数を同期実行する。

export interface RpcRequest {
  id: number
  method: string
  params: unknown
}
export interface RpcResponse {
  id: number
  result?: unknown
  error?: string
}

export class WorkerClient {
  private worker: Worker | null = null
  private seq = 0
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>()

  constructor(private create: () => Worker) {}

  private ensure(): Worker | null {
    if (this.worker) return this.worker
    try {
      this.worker = this.create()
    } catch {
      return null
    }
    this.worker.onmessage = (ev: MessageEvent<RpcResponse>) => {
      const p = this.pending.get(ev.data.id)
      if (!p) return
      this.pending.delete(ev.data.id)
      if (ev.data.error) p.reject(new Error(ev.data.error))
      else p.resolve(ev.data.result)
    }
    this.worker.onerror = e => {
      for (const p of this.pending.values()) p.reject(new Error(e.message))
      this.pending.clear()
      this.worker?.terminate()
      this.worker = null
    }
    return this.worker
  }

  get available(): boolean {
    return typeof Worker !== 'undefined' && this.ensure() !== null
  }

  call<T>(method: string, params: unknown, transfer: Transferable[] = []): Promise<T> {
    const w = this.ensure()
    if (!w) return Promise.reject(new Error('Worker が使えない'))
    const id = ++this.seq
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: v => resolve(v as T), reject })
      w.postMessage({ id, method, params } satisfies RpcRequest, transfer)
    })
  }

  terminate(): void {
    this.worker?.terminate()
    this.worker = null
    for (const p of this.pending.values()) p.reject(new Error('terminated'))
    this.pending.clear()
  }
}

/** Worker 側: メソッド表を登録して onmessage を張る */
export function serveWorker(methods: Record<string, (params: never) => unknown | Promise<unknown>>, transferOf?: (method: string, result: unknown) => Transferable[]): void {
  const scope = self as unknown as { onmessage: ((ev: MessageEvent<RpcRequest>) => void) | null; postMessage: (msg: RpcResponse, transfer?: Transferable[]) => void }
  scope.onmessage = async (ev: MessageEvent<RpcRequest>) => {
    const { id, method, params } = ev.data
    const fn = methods[method]
    if (!fn) {
      scope.postMessage({ id, error: `未知のメソッド: ${method}` })
      return
    }
    try {
      const result = await fn(params as never)
      scope.postMessage({ id, result }, transferOf ? transferOf(method, result) : [])
    } catch (e) {
      scope.postMessage({ id, error: e instanceof Error ? e.message : String(e) })
    }
  }
}
